/**
 * Generic form modal hook for handling form state, validation, and submission
 * Consolidates duplicated form logic from add-chart-modal and add-stat-card-modal
 */

import { useState, useCallback } from "react";

/**
 * Form state management for modal forms
 * Handles field updates, dimension updates, validation, and error tracking
 */
interface UseReportFormModalOptions<T> {
  emptyState: T;
  onValidate: (formState: T) => { isValid: boolean; errors: Record<string, string> };
}

/**
 * Generic hook for managing report modal form state
 * Reduces duplication between add-chart-modal and add-stat-card-modal
 *
 * @template T - The form state type
 * @param options - Configuration with empty state and validation function
 * @returns Form state, error state, and callbacks for field updates
 */
export function useReportFormModal<T extends Record<string, any>>(
  options: UseReportFormModalOptions<T>
) {
  const { emptyState, onValidate } = options;

  const [formState, setFormState] = useState<T>(emptyState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /**
   * Update a single field in the form state
   * Clears any errors for that field when updated
   */
  const updateField = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setFormState((prev) => ({
        ...prev,
        [field]: value,
      }));
      // Clear error for this field when user updates it
      if (formErrors[String(field)]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[String(field)];
          return newErrors;
        });
      }
    },
    [formErrors]
  );

  /**
   * Update a dimension at a specific index
   * Used for array of dimension selections in form state
   * Assumes form has a `dimensions` property that is an array
   */
  const updateDimension = useCallback((index: number, value: string | null) => {
    setFormState((prev: any) => {
      const newDimensions = [...prev.dimensions];
      newDimensions[index] = value;
      return { ...prev, dimensions: newDimensions };
    });
  }, []);

  /**
   * Validate form using provided validation function
   * Sets error state and returns validation result
   */
  const validateForm = useCallback((): boolean => {
    const { isValid, errors } = onValidate(formState);
    setFormErrors(errors);
    return isValid;
  }, [formState, onValidate]);

  /**
   * Reset form to empty state and clear errors
   */
  const resetForm = useCallback(() => {
    setFormState(emptyState);
    setFormErrors({});
  }, [emptyState]);

  /**
   * Populate form from editing config
   * Used when editing an existing item
   */
  const populateFromConfig = useCallback((config: Partial<T>) => {
    setFormState((prev) => ({
      ...prev,
      ...config,
    }));
    setFormErrors({});
  }, []);

  return {
    formState,
    setFormState,
    formErrors,
    setFormErrors,
    updateField,
    updateDimension,
    validateForm,
    resetForm,
    populateFromConfig,
  };
}
